import React from 'react';

export type CardVariant = 'default' | 'outlined' | 'flat' | 'elevated';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  isInteractive?: boolean;
}

const cardVariantStyles: Record<CardVariant, string> = {
  default: 'bg-white border border-[#E5E9E9] shadow-xs',
  outlined: 'bg-white border border-[#CBD5E1]',
  flat: 'bg-[#F7FAFA] border border-[#E5E9E9]',
  elevated: 'bg-white border border-[#E5E9E9] shadow-md',
};

const paddingStyles = {
  none: 'p-0',
  sm: 'p-3 sm:p-4',
  md: 'p-5 sm:p-6',
  lg: 'p-6 sm:p-8',
};

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ variant = 'default', padding = 'md', isInteractive = false, className = '', children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={`rounded-2xl transition-all duration-200 ${cardVariantStyles[variant]} ${paddingStyles[padding]} ${
          isInteractive
            ? 'hover:-translate-y-0.5 hover:shadow-md hover:border-[#1fb4ac]/40 cursor-pointer'
            : ''
        } ${className}`}
        {...props}
      >
        {children}
      </div>
    );
  }
);
Card.displayName = 'Card';

export const CardHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = '', children, ...props }) => (
  <div className={`flex flex-col space-y-1.5 mb-4 ${className}`} {...props}>
    {children}
  </div>
);

export const CardTitle: React.FC<React.HTMLAttributes<HTMLHeadingElement>> = ({ className = '', children, ...props }) => (
  <h3 className={`text-base sm:text-lg font-bold text-[#111111] leading-tight ${className}`} {...props}>
    {children}
  </h3>
);

export const CardDescription: React.FC<React.HTMLAttributes<HTMLParagraphElement>> = ({ className = '', children, ...props }) => (
  <p className={`text-xs sm:text-sm text-[#5F6368] leading-relaxed ${className}`} {...props}>
    {children}
  </p>
);

export const CardContent: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = '', children, ...props }) => (
  <div className={`space-y-4 ${className}`} {...props}>
    {children}
  </div>
);

export const CardFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = '', children, ...props }) => (
  <div className={`flex items-center justify-between pt-4 mt-4 border-t border-[#E5E9E9] ${className}`} {...props}>
    {children}
  </div>
);
